import type { SqlStatement, StorageAdapter } from "../adapter/adapter.js";

const TABLES = ["tasks", "recurrence_rules", "recurrence_overrides", "labels", "topics", "calendars"] as const;

/**
 * Zero `server_acked_mut` on every row of every synced table, so the whole
 * graph reads as server-dirty again.
 *
 * Called on sign-out (and account delete): the acks describe what ONE account's
 * server has been told. Signing into a different account with the acks intact
 * would leave every already-acknowledged row permanently invisible to its first
 * push — the new account's server has never seen them, but nothing would ever
 * send them. The companion to clearing the pull cursor, which forces the full
 * re-pull. Chunked through `executeBatch` where available so a native install
 * doesn't pay one IPC round-trip per row.
 */
export async function resetServerAcks(db: StorageAdapter): Promise<void> {
  for (const table of TABLES) {
    const rows = await db.select<{ id: string }>(`SELECT * FROM ${table}`);
    const statements: SqlStatement[] = rows
      .filter((r) => typeof r.id === "string")
      .map((r) => ({ sql: `UPDATE ${table} SET server_acked_mut=? WHERE id=?`, params: [0, r.id] }));
    const BATCH = 500;
    for (let i = 0; i < statements.length; i += BATCH) {
      const chunk = statements.slice(i, i + BATCH);
      if (db.executeBatch) await db.executeBatch(chunk);
      else for (const s of chunk) await db.execute(s.sql, s.params ?? []);
    }
  }
}
